import React from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {RoomSwitchRequiredTooltip} from 'components/Offer/Primary/RoomSwitchRequiredTooltip/RoomSwitchRequiredTooltip'
import {OfferInteraction} from 'components/Offer/Shared/OfferInteraction'
import {OfferLogo} from 'components/Offer/Shared/OfferLogo'
import {ProviderLogoComponent} from 'components/Offer/Shared/ProviderLogoComponent'
import {scrollToSection} from 'components/ScrollSpy'
import {useShouldOfferLeadToRooms} from 'hooks/useShouldOfferLeadToRooms'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {AccommodationPageNavSections} from 'modules/accommodation/types'
import {getAllOffersOverlay} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {getSplitBookingDetails} from 'modules/sapiSearch/selectors'
import {getAnchorPriceTotal} from 'modules/sapiSearch/utils'

import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {
  getCancellationDeadline,
  getRefundType
} from '@vio/core/src/offer/business/cancellationPenalties'
import isBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {getOfferPaymentMode} from '@vio/core/src/offer/business/offerPaymentMode'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {OfferPerks} from '@vio/shared/src/accommodation/OfferPerks/OfferPerks'

import {useAccommodationTopOffers} from '../useAccommodationTopOffers'
import {HotelOfferCardPriceComposition} from './HotelOfferCardPriceComposition'

type HotelOfferCardVariant = 'vertical' | 'horizontal'

interface Props {
  offer: SearchOffer
  variant?: HotelOfferCardVariant
  offerPosition: number
  sourceComponent?: string
}

// Capture phase: the parent OfferInteraction <a> calls stopPropagation on
// bubble, so we track the click here before it reaches the link.
const CtaCaptureWrapper = styled.span`
  display: block;
  width: 100%;
`

const ViewDealCta = ({
  offer,
  hotelId,
  offerPosition
}: {
  offer: SearchOffer
  hotelId: string
  offerPosition: number
}) => {
  const isTopOffer = offerPosition === 0
  const dispatchTeamEvent = useDispatchTeamEvent()

  const handleClick = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HeaderCTA,
        action: Action.Clicked,
        payload: {
          hotelId,
          providerCode: offer.providerCode,
          offerPosition,
          isTopOffer
        }
      })
    )
  }

  return (
    <CtaCaptureWrapper onClickCapture={handleClick}>
      <Button
        dataId="HeaderCTA"
        fullWidth
        size="lg"
        variant={isTopOffer ? 'primary' : 'tertiary'}
      >
        <FormattedMessageWrapper id="viewDeal" defaultMessage="View deal" />
      </Button>
    </CtaCaptureWrapper>
  )
}

const OfferProviderAndPerks = ({
  hotelId,
  offer,
  isSplitBooking = false,
  leadsToRooms = false
}: {
  hotelId: string
  offer: SearchOffer
  isSplitBooking?: boolean
  leadsToRooms?: boolean
}) => {
  const {brandCode} = useBrand()

  const splitBookingDetails = useSelector(state =>
    getSplitBookingDetails(state, hotelId ?? '')
  )

  const {splitBookingOffers, splitBookingBundle} = splitBookingDetails

  const cancellationPenalties = isSplitBooking
    ? splitBookingBundle?.cancellationPenalties || []
    : offer.cancellationPenalties || []

  const paymentType = getOfferPaymentMode({offer, isSplitBooking})

  return (
    <Box display="flex" flexDirection="column" gap="s300" width="100%">
      {isSplitBooking ? (
        <ProviderLogoComponent
          hotelId={hotelId}
          isSplitBooking
          splitBookingOffers={splitBookingOffers}
          variant="PrimaryDefault"
        />
      ) : (
        // Offers booked in-house (CTA scrolls to rooms) are on our own MOR, so
        // the logo stays visible via the brand code instead of being locked.
        <OfferLogo
          hotelId={hotelId}
          offer={offer}
          providerCodeOverride={leadsToRooms ? brandCode : undefined}
          variant="logo"
        />
      )}
      <Box display="flex" flexDirection="column" gap="s100" width="100%">
        <OfferPerks
          paymentType={paymentType}
          mealPlan={offer.package?.mealPlan}
          cancellationDeadline={
            getCancellationDeadline(cancellationPenalties) || ''
          }
          hideCancellationDeadline
          refundType={getRefundType(cancellationPenalties)}
          hideGuestCount
        />
        {isSplitBooking && <RoomSwitchRequiredTooltip variant="perk" />}
      </Box>
    </Box>
  )
}

export const HotelOfferCard: React.FC<Props> = ({
  offer,
  variant = 'vertical',
  offerPosition,
  sourceComponent = 'DesktopHotelViewTopOffers'
}) => {
  const dispatch = useDispatch()
  const hotelId = useSelector(getAccommodationPageHotelId)
  const overlay = useSelector(getAllOffersOverlay)
  const splitBookingDetails = useSelector(state =>
    getSplitBookingDetails(state, hotelId ?? '')
  )
  const {offerEntity} = useAccommodationTopOffers({hotelId})

  const {hasSplitBookingOffer, splitBookingPosition} = splitBookingDetails
  const isSplitBooking =
    offerPosition === splitBookingPosition && hasSplitBookingOffer

  const shouldOfferLeadToRooms = useShouldOfferLeadToRooms(
    isBofhOffer(offer) || isSplitBooking
  )

  const anchorPriceTotal = getAnchorPriceTotal(offerEntity)

  const scrollToRooms = () => {
    if (overlay) {
      dispatch(hideOverlay())
    }
    scrollToSection(AccommodationPageNavSections.Rooms, {
      deferred: Boolean(overlay)
    })
  }

  const providerAndPerks = (
    <OfferProviderAndPerks
      hotelId={hotelId}
      isSplitBooking={isSplitBooking}
      leadsToRooms={shouldOfferLeadToRooms}
      offer={offer}
    />
  )

  const content =
    variant === 'vertical' ? (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="s600"
        width="100%"
        height="100%"
      >
        {providerAndPerks}
        <Box
          display="flex"
          flexDirection="column"
          gap="s400"
          width="100%"
          justifyContent="space-between"
        >
          <HotelOfferCardPriceComposition
            hotelId={hotelId}
            isSplitBooking={isSplitBooking}
            offer={offer}
            size="xl"
          />
          <ViewDealCta
            offer={offer}
            hotelId={hotelId}
            offerPosition={offerPosition}
          />
        </Box>
      </Box>
    ) : (
      <Box
        display="flex"
        alignItems="flex-start"
        gap="s500"
        justifyContent="space-between"
        width="100%"
        height="100%"
      >
        <Box
          display="flex"
          flexDirection="column"
          gap="s300"
          flex="1 0 0"
          minWidth="0"
        >
          {providerAndPerks}
        </Box>
        <Box display="flex" flexDirection="column" gap="s400" flex="0 0 220px">
          <HotelOfferCardPriceComposition
            hotelId={hotelId}
            isSplitBooking={isSplitBooking}
            offer={offer}
            size="lg"
          />
          <ViewDealCta
            offer={offer}
            hotelId={hotelId}
            offerPosition={offerPosition}
          />
        </Box>
      </Box>
    )

  // BOFH offers are bookable in-house, so scroll to rooms instead of
  // redirecting out through OfferInteraction.
  if (shouldOfferLeadToRooms) {
    return (
      <Box
        data-id="APOfferScrollToRooms"
        onClick={scrollToRooms}
        role="button"
        style={{cursor: 'pointer', height: '100%'}}
        width="100%"
      >
        {content}
      </Box>
    )
  }

  return (
    <OfferInteraction
      anchorPriceTotal={anchorPriceTotal}
      component={sourceComponent}
      hasInteraction
      hotelId={hotelId}
      offer={offer}
      offerPosition={offerPosition}
    >
      {content}
    </OfferInteraction>
  )
}
